import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../api';

/**
 * Session + role capabilities pulled from /api/auth/me. Components
 * use the `useSession()` and `useCap()` hooks; nav and routes use
 * the capability set to gate what shows.
 */

const SessionContext = createContext({
  loading: true,
  user: null,
  reload: () => {},
});

export function SessionProvider({ children }) {
  const [state, setState] = useState({ loading: true, user: null });

  const reload = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await api.auth.me();
      if (data.authenticated) {
        setState({
          loading: false,
          user: {
            email:        data.email,
            name:         data.name || data.email,
            picture:      data.picture,
            role:         data.role,
            capabilities: new Set(data.capabilities || []),
          },
        });
      } else {
        setState({ loading: false, user: null });
      }
    } catch (e) {
      setState({ loading: false, user: null });
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <SessionContext.Provider value={{ ...state, reload }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}

/** True if the current user has every capability in `caps`. */
export function useCap(...caps) {
  const { user } = useSession();
  if (!user) return false;
  return caps.every((c) => user.capabilities.has(c));
}

/** True if the current user has any of the capabilities listed. */
export function useAnyCap(...caps) {
  const { user } = useSession();
  if (!user) return false;
  return caps.some((c) => user.capabilities.has(c));
}

/* Capability constants — keep in sync with backend/auth/roles.py Cap enum. */
export const CAP = {
  VIEW_EMPLOYEES:       'view_employees',
  VIEW_CTC:             'view_ctc',
  VIEW_SALARY_HISTORY:  'view_salary_history',
  VIEW_PROJECTS:        'view_projects',
  VIEW_PROJECT_PROFIT:  'view_project_profit',
  VIEW_RATE_CARD:       'view_rate_card',
  VIEW_OVERHEAD:        'view_overhead',
  VIEW_TIMESHEETS_ALL:  'view_timesheets_all',
  VIEW_TIMESHEETS_OWN:  'view_timesheets_own',
  VIEW_KPI:             'view_kpi',
  VIEW_RECON:           'view_recon',
  EDIT_RATE_CARD:       'edit_rate_card',
  EDIT_OVERHEAD:        'edit_overhead',
  EDIT_CTC:             'edit_ctc',
  EDIT_PROJECT_PIN:     'edit_project_pin',
  EDIT_OVERTIME:        'edit_overtime',
};
